import { useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ROLE_LABEL,
  supabase,
  TIER_LABEL,
  type Invitation,
  type Organisation,
  type PartnerTier,
  type SubscriptionStatus,
} from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import { invokeFunction } from '@/lib/invoke'
import { Badge, Button, Card, ErrorNote, PageHeader } from '@/components/ui'
import { InvitationActions } from '@/components/InvitationActions'

const TIERS: PartnerTier[] = ['core', 'supported', 'partner_plus']
const STATUSES: SubscriptionStatus[] = ['pending', 'active', 'grace', 'lapsed', 'cancelled']
const PURCHASER_TYPES: Array<'community' | 'corporate'> = ['community', 'corporate']

// Kebab-cased slug from an organisation name. Slug appears in nothing partner
// facing yet - it exists because organisations.slug is unique-not-null; a stable
// one derived from the name means creating an organisation does not need one
// more decision.
function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
}

/**
 * R8, work plan 2.7 and 2.3. KTM admins create partner organisations and
 * invite their first admin without touching the SQL editor. Facilitators are
 * added later by that org admin from /team - one invite path, one function.
 */
export function OrganisationsPage() {
  const { isKtmAdmin } = useAuth()
  const queryClient = useQueryClient()

  const { data: organisations, isLoading } = useQuery({
    queryKey: ['organisations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organisations')
        .select('id, name, slug, tier, purchaser_type, subscription_status, renews_at, grace_ends_at, data_agreement_signed_at')
        .order('name')
      if (error) throw error
      return data as Organisation[]
    },
  })

  const { data: invitations } = useQuery({
    queryKey: ['invitations', 'ktm'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitations')
        .select('id, email, org_id, role, created_at, expires_at, accepted_at, invited_by')
        .is('accepted_at', null)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as Invitation[]
    },
  })

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Organisation> }) => {
      const { error } = await supabase.from('organisations').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organisations'] }),
  })

  return (
    <>
      <PageHeader title="Partner organisations">
        Tier drives what a partner can open. Subscription status drives whether they can open
        anything at all. Open an organisation for its contacts, people, invitations and delete.
      </PageHeader>

      {isKtmAdmin && (
        <div className="mb-6 grid gap-4 lg:grid-cols-2">
          <CreateOrganisationCard />
          <InviteOrgAdminCard organisations={organisations ?? []} />
        </div>
      )}

      {isLoading && <p className="text-sm text-inksoft">Loading…</p>}

      <h2 className="mb-3 text-base text-ink">Organisations</h2>
      <div className="mb-8 overflow-x-auto rounded-md border border-line bg-surface">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-inksoft">
            <tr>
              <th className="px-4 py-2 font-medium">Organisation</th>
              <th className="px-4 py-2 font-medium">Tier</th>
              <th className="px-4 py-2 font-medium">Subscription</th>
              <th className="px-4 py-2 font-medium">Renews</th>
              <th className="px-4 py-2 font-medium">Data agreement</th>
            </tr>
          </thead>
          <tbody>
            {(organisations ?? []).map((org) => (
              <tr key={org.id} className="border-b border-line last:border-0">
                <td className="px-4 py-2">
                  <Link
                    to={`/admin/organisations/${org.id}`}
                    className="text-pounamu underline underline-offset-4 hover:no-underline"
                  >
                    {org.name}
                  </Link>
                  <div className="text-xs text-inksoft">{org.slug}</div>
                </td>
                <td className="px-4 py-2">
                  {isKtmAdmin ? (
                    <select
                      aria-label={`Tier for ${org.name}`}
                      value={org.tier}
                      onChange={(e) =>
                        update.mutate({ id: org.id, patch: { tier: e.target.value as PartnerTier } })
                      }
                      className="rounded-sm border border-line px-2 py-1"
                    >
                      {TIERS.map((t) => (
                        <option key={t} value={t}>{TIER_LABEL[t]}</option>
                      ))}
                    </select>
                  ) : (
                    TIER_LABEL[org.tier]
                  )}
                </td>
                <td className="px-4 py-2">
                  {isKtmAdmin ? (
                    <select
                      aria-label={`Subscription status for ${org.name}`}
                      value={org.subscription_status}
                      onChange={(e) =>
                        update.mutate({
                          id: org.id,
                          patch: { subscription_status: e.target.value as SubscriptionStatus },
                        })
                      }
                      className="rounded-sm border border-line px-2 py-1"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    org.subscription_status
                  )}
                </td>
                <td className="px-4 py-2 text-inksoft">
                  {org.renews_at ? new Date(org.renews_at).toLocaleDateString('en-NZ') : '—'}
                </td>
                <td className="px-4 py-2">
                  <Badge tone={org.data_agreement_signed_at ? 'good' : 'alert'}>
                    {org.data_agreement_signed_at ? 'Signed' : 'Not recorded'}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PendingInvitationsTable
        invitations={invitations ?? []}
        organisations={organisations ?? []}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Create organisation
// ---------------------------------------------------------------------------
function CreateOrganisationCard() {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [tier, setTier] = useState<PartnerTier>('core')
  const [purchaserType, setPurchaserType] =
    useState<'community' | 'corporate'>('community')
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>('pending')
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null)

  const slug = useMemo(() => slugify(name), [name])

  const create = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Give the organisation a name.')
      if (!slug) throw new Error('That name does not produce a URL slug. Add letters or numbers.')
      const { data, error: insertError } = await supabase
        .from('organisations')
        .insert({
          name: name.trim(),
          slug,
          tier,
          purchaser_type: purchaserType,
          subscription_status: subscriptionStatus,
        })
        .select('id, name')
        .single()
      if (insertError) {
        if (insertError.code === '23505') {
          throw new Error('An organisation with that slug already exists.')
        }
        throw insertError
      }
      return data
    },
    onSuccess: (row) => {
      setCreated(row!)
      setName('')
      setTier('core')
      setPurchaserType('community')
      setSubscriptionStatus('pending')
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['organisations'] })
    },
    onError: (e: Error) => {
      setError(e.message)
      setCreated(null)
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    create.mutate()
  }

  return (
    <Card>
      <h2 className="text-base text-ink">Add a partner organisation</h2>
      <p className="mt-1 text-sm text-inksoft">
        Creates the record so you can invite its admin. Set the subscription status to
        active only when the agreement is in place; new partners usually start as pending.
      </p>
      <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-ink">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            required
          />
          {slug && (
            <span className="mt-1 block text-xs text-inksoft">URL slug: {slug}</span>
          )}
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ink">Tier</span>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as PartnerTier)}
            className="mt-1 w-full rounded-sm border border-line px-3 py-2"
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>{TIER_LABEL[t]}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ink">Purchaser type</span>
          <select
            value={purchaserType}
            onChange={(e) =>
              setPurchaserType(e.target.value as 'community' | 'corporate')
            }
            className="mt-1 w-full rounded-sm border border-line px-3 py-2"
          >
            {PURCHASER_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-ink">Subscription status</span>
          <select
            value={subscriptionStatus}
            onChange={(e) => setSubscriptionStatus(e.target.value as SubscriptionStatus)}
            className="mt-1 w-full rounded-sm border border-line px-3 py-2"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <div className="sm:col-span-2">
          {error && <div className="mb-3"><ErrorNote>{error}</ErrorNote></div>}
          {created && (
            <p className="mb-3 text-sm text-pounamu">
              Added {created.name}.{' '}
              <Link to={`/admin/organisations/${created.id}`} className="underline underline-offset-4">
                Open it
              </Link>{' '}
              to fill in contacts and invite its admin.
            </p>
          )}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add organisation'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Invite an organisation admin
// ---------------------------------------------------------------------------
function InviteOrgAdminCard({ organisations }: { organisations: Organisation[] }) {
  const queryClient = useQueryClient()
  const [orgId, setOrgId] = useState('')
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)

  const invite = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error('Pick the organisation this person will administer.')
      await invokeFunction('invite-user', { email, fullName, orgId, role: 'org_admin' })
    },
    onSuccess: () => {
      setSent(email)
      setEmail('')
      setFullName('')
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['invitations', 'ktm'] })
    },
    onError: (e: Error) => {
      setError(e.message || 'That invitation could not be sent.')
      setSent(null)
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    invite.mutate()
  }

  return (
    <Card>
      <h2 className="text-base text-ink">Invite an organisation admin</h2>
      <p className="mt-1 text-sm text-inksoft">
        Sends a sign-in link. Once they accept it, they can invite their own facilitators from /team.
      </p>
      <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-ink">Organisation</span>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            required
          >
            <option value="">Choose an organisation…</option>
            {organisations.map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ink">Full name</span>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="mt-1 w-full rounded-sm border border-line px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ink">Email address</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-sm border border-line px-3 py-2"
          />
        </label>
        <div className="sm:col-span-2">
          {error && <div className="mb-3"><ErrorNote>{error}</ErrorNote></div>}
          {sent && <p className="mb-3 text-sm text-pounamu">Invitation sent to {sent}.</p>}
          <Button type="submit" disabled={invite.isPending}>
            {invite.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Pending invitations, with revoke
// ---------------------------------------------------------------------------
function PendingInvitationsTable({
  invitations,
  organisations,
}: {
  invitations: Invitation[]
  organisations: Organisation[]
}) {
  const queryClient = useQueryClient()
  const orgById = useMemo(
    () => new Map(organisations.map((o) => [o.id, o.name])),
    [organisations],
  )
  const [error, setError] = useState<string | null>(null)
  const refresh = () => {
    setError(null)
    void queryClient.invalidateQueries({ queryKey: ['invitations', 'ktm'] })
  }

  return (
    <>
      <h2 className="mb-3 text-base text-ink">Pending invitations</h2>
      {error && <div className="mb-3"><ErrorNote>{error}</ErrorNote></div>}
      {invitations.length === 0 ? (
        <p className="rounded-md border border-dashed border-line bg-surface px-4 py-6 text-center text-sm text-inksoft">
          No invitations are awaiting acceptance.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-inksoft">
              <tr>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Organisation</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Sent</th>
                <th className="px-4 py-2 font-medium">Expires</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((invite) => {
                const expired = new Date(invite.expires_at) < new Date()
                return (
                  <tr key={invite.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-2">{invite.email}</td>
                    <td className="px-4 py-2 text-inksoft">
                      {orgById.get(invite.org_id) ?? invite.org_id}
                    </td>
                    <td className="px-4 py-2 text-inksoft">{ROLE_LABEL[invite.role]}</td>
                    <td className="px-4 py-2 text-inksoft">
                      {new Date(invite.created_at).toLocaleDateString('en-NZ')}
                    </td>
                    <td className="px-4 py-2">
                      {expired ? (
                        <Badge tone="alert">Expired</Badge>
                      ) : (
                        <span className="text-inksoft">
                          {new Date(invite.expires_at).toLocaleDateString('en-NZ')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <InvitationActions invitation={invite} onChanged={refresh} onError={setError} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
